import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Tooltip,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Fade,
  Select,
  FormControl,
  InputLabel,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Skeleton,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
} from '@mui/icons-material';
import { userAPI, screenAPI } from '../services/api';
import { toast } from 'react-toastify';
// Removed legacy brandColors import as we use theme now

const Users = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [users, setUsers] = useState([]);
  const [screens, setScreens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openUrlDialog, setOpenUrlDialog] = useState(false);
  const [displayUrls, setDisplayUrls] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    contactNumber: '',
    screenIds: [], // Changed from screenId to screenIds array
    isActive: true,
  });

  useEffect(() => {
    fetchUsers();
    fetchScreens();
  }, [page, rowsPerPage, searchQuery]);

  // Re-map users with screens once screens are loaded or updated
  useEffect(() => {
    if (screens.length > 0 && users.length > 0) {
      setUsers(currentUsers => {
        const usersWithScreens = currentUsers.map(user => {
          const assignedScreens = screens.filter(screen => {
            const screenUserId = typeof screen.assignedUserId === 'object'
              ? screen.assignedUserId?._id
              : screen.assignedUserId;
            return screenUserId === user._id;
          });
          
          const newScreenIds = assignedScreens.map(s => s._id);
          const newScreenNames = assignedScreens.map(s => s.screenName).join(', ') || 'No screens';
          
          // Only update if something actually changed
          if (JSON.stringify(user.screenIds) === JSON.stringify(newScreenIds) && 
              user.screenNames === newScreenNames) {
            return user;
          }
          
          return {
            ...user,
            screenNames: newScreenNames,
            screenIds: newScreenIds
          };
        });
        
        // If no user objects actually changed, return currentUsers to avoid re-render
        const anyChanged = usersWithScreens.some((u, i) => u !== currentUsers[i]);
        return anyChanged ? usersWithScreens : currentUsers;
      });
    }
  }, [screens, users.length]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getAllUsers({
        page: page + 1,
        limit: rowsPerPage,
        search: searchQuery,
      });
      const usersData = response.data.data.users || [];
      setUsers(usersData);
      setTotalUsers(response.data.data.pagination.total || 0);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchScreens = async () => {
    try {
      const response = await screenAPI.getAllScreens({ limit: 100 });
      const screensData = response.data.data.screens || [];
      console.log('Fetched screens:', screensData);
      setScreens(screensData);
    } catch (error) {
      console.error('Error fetching screens:', error);
      toast.error('Failed to load screens');
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      const userScreenIds = user.screenIds || [];
      
      setSelectedUser(user);
      setFormData({
        username: user.fullName || user.username,
        email: user.email,
        password: '',
        contactNumber: user.contactNumber || '',
        screenIds: userScreenIds,
        isActive: user.status === 'active',
      });
    } else {
      setSelectedUser(null);
      setFormData({
        username: '',
        email: '',
        password: '',
        contactNumber: '',
        screenIds: [],
        isActive: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
    setFormData({
      username: '',
      email: '',
      password: '',
      contactNumber: '',
      screenIds: [],
      isActive: true,
    });
  };

  const handleSubmit = async () => {
    try {
      if (!formData.username || !formData.email) {
        toast.error('Username and email are required');
        return;
      }

      if (!formData.contactNumber) {
        toast.error('Contact number is required');
        return;
      }

      if (!selectedUser && !formData.password) {
        toast.error('Password is required for new users');
        return;
      }

      // Determine screen size from first selected screen or use default
      let screenSize = {
        width: 1920,
        height: 1080,
        label: '1920x1080'
      };

      if (formData.screenIds && formData.screenIds.length > 0) {
        const firstScreen = screens.find(s => s._id === formData.screenIds[0]);
        if (firstScreen && firstScreen.screenSize) {
          screenSize = {
            width: firstScreen.screenSize.width || 1920,
            height: firstScreen.screenSize.height || 1080,
            label: firstScreen.screenSize.label || '1920x1080'
          };
        }
      }

      const submitData = {
        fullName: formData.username,
        email: formData.email,
        contactNumber: formData.contactNumber,
        screenSize: screenSize,
        status: formData.isActive ? 'active' : 'inactive'
      };

      // Add password only if provided (for new users or password change)
      if (formData.password) {
        submitData.password = formData.password;
      }

      console.log('Submitting user data:', submitData);
      console.log('Selected screen IDs:', formData.screenIds);
      console.log('Selected user:', selectedUser);

      if (selectedUser) {
        // Update existing user
        const response = await userAPI.updateUser(selectedUser._id, submitData);

        // Update screen assignments if changed
        const oldScreenIds = selectedUser.screenIds || [];
        const newScreenIds = formData.screenIds;

        // Find screens to unassign (in old but not in new)
        const screensToUnassign = oldScreenIds.filter(id => !newScreenIds.includes(id));
        // Find screens to assign (in new but not in old)
        const screensToAssign = newScreenIds.filter(id => !oldScreenIds.includes(id));

        try {
          // Unassign removed screens
          for (const screenId of screensToUnassign) {
            try {
              await screenAPI.unassignScreen(screenId);
            } catch (unassignError) {
              console.error('Error unassigning screen:', screenId, unassignError);
            }
          }

          // Assign new screens
          for (const screenId of screensToAssign) {
            try {
              await screenAPI.assignScreen(screenId, selectedUser._id);
            } catch (assignError) {
              console.error('Error assigning screen:', screenId, assignError);
            }
          }

          if (screensToUnassign.length > 0 || screensToAssign.length > 0) {
            toast.success('User and screen assignments updated successfully');
          } else {
            toast.success('User updated successfully');
          }
        } catch (assignError) {
          console.error('Error updating screen assignments:', assignError);
          toast.warning('User updated but some screen assignments failed');
        }
      } else {
        // Create new user
        const response = await userAPI.createUser(submitData);
        const newUser = response.data.data.user;

        // Assign all selected screens to the new user (if any)
        if (formData.screenIds && formData.screenIds.length > 0) {
          try {
            let successCount = 0;
            for (const screenId of formData.screenIds) {
              try {
                await screenAPI.assignScreen(screenId, newUser._id);
                successCount++;
              } catch (assignError) {
                console.error('Error assigning screen:', screenId, assignError);
              }
            }

            if (successCount === formData.screenIds.length) {
              toast.success(`User created and ${successCount} screen(s) assigned successfully`);
            } else {
              toast.warning(`User created but only ${successCount}/${formData.screenIds.length} screens assigned`);
            }
          } catch (assignError) {
            console.error('Error assigning screens:', assignError);
            toast.warning('User created but screen assignment failed');
          }
        } else {
          toast.success('User created successfully (no screens assigned)');
        }
      }

      handleCloseDialog();
      // Small delay to ensure database is updated before fetching
      setTimeout(() => {
        fetchUsers();
      }, 500);
    } catch (error) {
      console.error('Error saving user:', error);
      toast.error(error.response?.data?.message || 'Failed to save user');
    }
  };

  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await userAPI.deleteUser(selectedUser._id);
      toast.success('User deleted successfully');
      setOpenDeleteDialog(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.message || 'Failed to delete user');
    }
  };

  const handleToggleStatus = async (user) => {
    try {
      await userAPI.toggleUserStatus(user._id);
      toast.success(`User ${user.status === 'active' ? 'deactivated' : 'activated'} successfully`);
      fetchUsers();
    } catch (error) {
      console.error('Error toggling user status:', error);
      toast.error('Failed to update user status');
    }
  };

  const handleCopyDisplayUrl = async (user) => {
    try {
      // Get all screens assigned to this user with their display URLs
      const response = await userAPI.getUserScreens(user._id);
      const userScreens = response.data.data?.screens || [];

      if (userScreens.length === 0) {
        toast.warning('No screens assigned to this user');
        return;
      }

      // If user has multiple screens, show dialog with all URLs
      if (userScreens.length > 1) {
        const urls = userScreens.map(screen => ({
          screenName: screen.screenName,
          location: screen.location,
          url: screen.displayUrl || `${process.env.REACT_APP_API_URL}/display/${screen.screenMediaUrl}`
        }));
        setDisplayUrls(urls);
        setSelectedUser(user);
        setOpenUrlDialog(true);
      } else {
        // Single screen - copy directly
        const url = userScreens[0].displayUrl || `${process.env.REACT_APP_API_URL}/display/${userScreens[0].screenMediaUrl}`;

        if (!url) {
          throw new Error('Display URL not found');
        }

        await navigator.clipboard.writeText(url);
        toast.success('Display URL copied to clipboard');
      }
    } catch (error) {
      console.error('Error copying URL:', error);
      toast.error('Failed to copy display URL. Please try again.');
    }
  };

  const handleCopySingleUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Display URL copied to clipboard');
    } catch (error) {
      console.error('Error copying URL:', error);
      toast.error('Failed to copy URL');
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      {/* Enhanced Header */}
      <Fade in={true} timeout={500}>
        <Box sx={{ mb: 4 }}>
          <Typography
            variant={isMobile ? 'h5' : 'h4'}
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              mb: 0.5,
            }}
          >
            User Management
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Manage users and assign screens
          </Typography>

          <Box sx={{
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <TextField
              size="small"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              variant="outlined"
              sx={{
                flex: { xs: '1 1 100%', sm: '1 1 auto' },
                maxWidth: { sm: 280 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                  fontSize: '0.9rem',
                  backgroundColor: '#f8fafc',
                  '& fieldset': {
                    borderColor: '#e2e8f0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#cbd5e1',
                  },
                },
                '& .MuiOutlinedInput-input': {
                  padding: '10px 14px',
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: { xs: '1 1 100%', sm: '0 1 auto' } }}>
              <Tooltip title="Refresh table" arrow>
                <IconButton
                  size="small"
                  onClick={fetchUsers}
                  color="primary"
                  sx={{
                    borderRadius: 1.5,
                    '&:hover': {
                      backgroundColor: 'rgba(124, 58, 237, 0.08)',
                    },
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                sx={{
                  backgroundColor: '#7C3AED',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#6D28D9',
                  },
                }}
              >
                Add User
              </Button>
            </Box>
          </Box>
        </Box>
      </Fade>

      <Fade in={true} timeout={700}>
        <TableContainer
          component={Paper}
          sx={{
            boxShadow: theme.shadows[1],
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: 'background.default',
                  '& .MuiTableCell-head': {
                    color: 'text.secondary',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                    letterSpacing: '0.5px',
                    padding: '14px 16px',
                  },
                }}
              >
                <TableCell sx={{ width: '18%' }}>Username</TableCell>
                <TableCell sx={{ width: '22%' }}>Email</TableCell>
                <TableCell sx={{ width: '22%' }}>Screens</TableCell>
                <TableCell sx={{ width: '12%' }}>Status</TableCell>
                <TableCell sx={{ width: '15%' }}>Created</TableCell>
                <TableCell align="center" sx={{ width: '11%', py: 1.75 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: rowsPerPage }).map((_, index) => (
                  <TableRow
                    key={`skeleton-${index}`}
                    sx={{
                      '& .MuiTableCell-root': {
                        padding: '12px 16px',
                        verticalAlign: 'middle',
                      },
                    }}
                  >
                    <TableCell sx={{ width: '18%' }}>
                      <Skeleton variant="text" width="80%" height={20} />
                    </TableCell>
                    <TableCell sx={{ width: '22%' }}>
                      <Skeleton variant="text" width="85%" height={20} />
                    </TableCell>
                    <TableCell sx={{ width: '22%' }}>
                      <Skeleton variant="rounded" width="60%" height={24} />
                    </TableCell>
                    <TableCell sx={{ width: '12%' }}>
                      <Skeleton variant="rounded" width="70%" height={24} />
                    </TableCell>
                    <TableCell sx={{ width: '15%' }}>
                      <Skeleton variant="text" width="50%" height={20} />
                    </TableCell>
                    <TableCell sx={{ width: '11%' }} align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <Skeleton variant="circular" width={32} height={32} />
                        <Skeleton variant="circular" width={32} height={32} />
                        <Skeleton variant="circular" width={32} height={32} />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                      No users found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {searchQuery ? 'Try adjusting your search' : 'Click "Add User" to create your first user'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user, index) => (
                  <TableRow
                    key={user._id}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                      transition: 'all 0.2s ease',
                      '& .MuiTableCell-root': {
                        padding: '12px 16px',
                        verticalAlign: 'middle',
                      },
                    }}
                  >
                    <TableCell sx={{ width: '18%' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                        {user.fullName || user.username}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ width: '22%' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                        {user.email}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ width: '22%' }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {user.screenIds && user.screenIds.length > 0 ? (
                          user.screenIds.length > 2 ? (
                            <Chip
                              label={`${user.screenIds.length} screens`}
                              size="small"
                              sx={{
                                backgroundColor: '#f1f5f9',
                                color: '#64748b',
                                fontWeight: 600,
                                fontSize: '0.8rem',
                              }}
                            />
                          ) : (
                            user.screenNames.split(', ').map((name, idx) => (
                              <Chip
                                key={idx}
                                label={name.charAt(0).toUpperCase() + name.slice(1).toLowerCase()}
                                size="small"
                                sx={{
                                  backgroundColor: '#e0e7ff',
                                  color: '#4338ca',
                                  fontWeight: 600,
                                  fontSize: '0.8rem',
                                  height: '24px',
                                }}
                              />
                            ))
                          )
                        ) : (
                          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            —
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ width: '12%' }}>
                      <Chip
                        icon={user.status === 'active' ? <ActiveIcon /> : <InactiveIcon />}
                        label={user.status === 'active' ? 'Active' : 'Inactive'}
                        size="small"
                        onClick={() => handleToggleStatus(user)}
                        sx={{
                          cursor: 'pointer',
                          fontWeight: 600,
                          fontSize: '0.8rem',
                          backgroundColor: user.status === 'active' ? '#dcfce7' : '#f5f5f5',
                          color: user.status === 'active' ? '#22c55e' : '#9ca3af',
                          border: user.status === 'active' ? '1px solid #86efac' : '1px solid #e5e7eb',
                          height: '24px',
                          '& .MuiChip-icon': {
                            fontSize: '1rem !important',
                          },
                          '&:hover': {
                            backgroundColor: user.status === 'active' ? '#bbf7d0' : '#f0f0f0',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ width: '15%' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                        {new Date(user.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ width: '11%' }}>
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <Tooltip title="Copy Display URL" placement="top" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleCopyDisplayUrl(user)}
                            color="primary"
                            sx={{
                              padding: '6px',
                              '&:hover': {
                                backgroundColor: 'rgba(124, 58, 237, 0.1)',
                              },
                              transition: 'all 0.2s ease',
                            }}
                          >
                            <CopyIcon sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit User" placement="top" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(user)}
                            color="primary"
                            sx={{
                              padding: '6px',
                              '&:hover': {
                                backgroundColor: 'rgba(124, 58, 237, 0.1)',
                              },
                              transition: 'all 0.2s ease',
                            }}
                          >
                            <EditIcon sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete User" placement="top" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(user)}
                            sx={{
                              padding: '6px',
                              color: '#ef4444',
                              '&:hover': {
                                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                              },
                              transition: 'all 0.2s ease',
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={totalUsers}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              borderTop: '1px solid #e2e8f0',
              backgroundColor: '#fafbfc',
              '& .MuiTablePagination-toolbar': {
                minHeight: '56px',
                padding: '8px 16px',
              },
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                margin: '0 !important',
                fontSize: '0.9rem',
              },
            }}
          />
        </TableContainer>
      </Fade>

      {/* Create/Edit User Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: theme.shadows[4],
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: 'primary.main',
            color: 'white',
            fontWeight: 700,
            fontSize: '1.25rem',
          }}
        >
          {selectedUser ? 'Edit User' : 'Create New User'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Username"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Contact Number"
              type="tel"
              value={formData.contactNumber}
              onChange={(e) => setFormData({ ...formData, contactNumber: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Password"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              fullWidth
              required={!selectedUser}
              helperText={selectedUser ? 'Leave blank to keep current password' : ''}
            />
            <FormControl fullWidth required>
              <InputLabel id="screen-select-label">Screens</InputLabel>
              <Select
                labelId="screen-select-label"
                multiple
                value={formData.screenIds}
                onChange={(e) => setFormData({ ...formData, screenIds: e.target.value })}
                input={<OutlinedInput label="Screens" />}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return <em style={{ color: '#999' }}>Select screens</em>;
                  }
                  const selectedScreens = selected
                    .map(id => {
                      const screen = screens.find(s => s._id === id);
                      if (!screen) return '';
                      // Show "Screen Name (Location)" format
                      return screen.location
                        ? `${screen.screenName} (${screen.location})`
                        : screen.screenName;
                    })
                    .filter(Boolean);

                  if (selectedScreens.length > 2) {
                    return `${selectedScreens.length} screens selected`;
                  }
                  return selectedScreens.join(', ');
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 400,
                    },
                  },
                }}
              >
                {screens
                  .filter(screen => {
                    // Always show screens that are already selected in the form
                    if (formData.screenIds.includes(screen._id)) return true;
                    
                    // Show if unassigned
                    if (!screen.assignedUserId) return true;

                    // Handle both populated object and string ID
                    const screenUserId = typeof screen.assignedUserId === 'object'
                      ? screen.assignedUserId?._id
                      : screen.assignedUserId;

                    // Show if already assigned to this user
                    return screenUserId === selectedUser?._id;
                  })
                  .map((screen) => (
                    <MenuItem
                      key={screen._id}
                      value={screen._id}
                      sx={{
                        py: 1.5,
                        '&:hover': {
                          backgroundColor: 'action.hover', // Usage of standard palette action
                        },
                      }}
                    >
                      <Checkbox
                        checked={formData.screenIds.indexOf(screen._id) > -1}
                        sx={{
                          color: 'primary.main',
                          '&.Mui-checked': {
                            color: 'primary.main',
                          },
                        }}
                      />
                      <ListItemText
                        primary={
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {screen.screenName}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            📍 {screen.location || 'No location specified'}
                          </Typography>
                        }
                      />
                    </MenuItem>
                  ))}
                {screens.filter(screen => {
                  if (formData.screenIds.includes(screen._id)) return true;
                  if (!screen.assignedUserId) return true;
                  const screenUserId = typeof screen.assignedUserId === 'object'
                    ? screen.assignedUserId?._id
                    : screen.assignedUserId;
                  return screenUserId === selectedUser?._id;
                }).length === 0 && (
                    <MenuItem disabled>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center', width: '100%' }}>
                        No screens available. All screens are assigned to other users.
                      </Typography>
                    </MenuItem>
                  )}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                Select one or more screens to assign to this user
              </Typography>
            </FormControl>
            <TextField
              label="Status"
              select
              value={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.value })}
              fullWidth
            >
              <MenuItem value={true}>Active</MenuItem>
              <MenuItem value={false}>Inactive</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleCloseDialog}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            sx={{
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: theme.shadows[4],
              },
              transition: 'all 0.2s ease',
            }}
          >
            {selectedUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: theme.shadows[4],
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: 'error.main', // Standard error color
            color: 'white',
            fontWeight: 700,
            fontSize: '1.25rem',
          }}
        >
          Confirm Delete
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete user <strong>"{selectedUser?.fullName || selectedUser?.username}"</strong>?
          </Typography>
          <Typography variant="body2" color="error">
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setOpenDeleteDialog(false)}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error" // Standard error button
            sx={{
              '&:hover': {
                backgroundColor: 'error.dark',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 20px rgba(239, 68, 68, 0.3)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Display URLs Dialog (for multiple screens) */}
      <Dialog
        open={openUrlDialog}
        onClose={() => setOpenUrlDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: theme.shadows[4],
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: 'primary.main',
            color: 'white',
            fontWeight: 700,
            fontSize: '1.25rem',
          }}
        >
          Display URLs for {selectedUser?.fullName || selectedUser?.username}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            This user has multiple screens assigned. Click on any URL to copy it.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {displayUrls.map((item, index) => (
              <Paper
                key={index}
                sx={{
                  p: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    borderColor: 'primary.main',
                  },
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                }}
                onClick={() => handleCopySingleUrl(item.url)}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {item.screenName}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopySingleUrl(item.url);
                    }}
                    color="primary"
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(124, 58, 237, 0.1)',
                      },
                    }}
                  >
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </Box>
                {item.location && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    📍 {item.location}
                  </Typography>
                )}
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    fontFamily: 'monospace',
                    fontSize: '0.85rem',
                    wordBreak: 'break-all',
                    backgroundColor: 'action.hover',
                    p: 1,
                    borderRadius: 1,
                  }}
                >
                  {item.url}
                </Typography>
              </Paper>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setOpenUrlDialog(false)}
            variant="contained"
            color="primary"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;